import React, { useEffect, useState, useRef } from "react";
import { useUsermaven } from "@usermaven/react";
import { useNavigate } from "react-router";
import gsap from "gsap";

// Heartbeat
import { Heartbeat } from "../helpers/heartbeat";
import { usePageView } from "@usermaven/react";

// GUI
import ButtonGoProfile from "../helpers/GUI/buttonGoProfile";
import animations_gsap from "../helpers/animations";

// import profiles
import ProfilesMenu from "../helpers/GUI/ProfilesMenu";
import HeaderMenu from "../helpers/GUI/HeaderMenu";

// check debugger disabled profile
import Debugger from "../helpers/debugger";
import DebuggerGUI from "../helpers/GUI/DebuggerGUIProps";
import {
  getParsedHashtableProfiles,
  setHasTableProfilesStorage,
} from "../helpers/disabledProfile";

type AcceptedProfiles = "ABIGAIL" | "JEREMY" | "MARK";
type HashtableProfiles = {
  profile: AcceptedProfiles;
  isDisabled: boolean;
};

interface ScreenSaverProps {
  triggerTransition: (to: string) => void;
}

const PathwayMenu: React.FC<ScreenSaverProps> = ({ triggerTransition }) => {
  let navigate = useNavigate();
  usePageView();
  const { track } = useUsermaven();

  // Controller for instance Heartbeat
  const myHeartbeat = useRef<Heartbeat | null>(null);

  const [remainingTime, setRemainingTime] = useState<Number | null>(null);
  const [progress, setProgress] = useState<Number | null>(null);
  const [askisngStillThere, setAskisngStillThere] = useState<Boolean | null>(
    null
  );
  const [redirectUser, setRedirectUser] = useState<Boolean | null>(false);
  const [goToProfile, setGoToProfile] = useState<AcceptedProfiles | null>(null);

  const [showDebugger, setShowDebugger] = useState<boolean>(false);
  const [hashTableProfiles, setHashTableProfiles] = useState(
    getParsedHashtableProfiles()
  );

  const handleTimeout = () => {
    setRedirectUser(true);
    track('Reset Experience', {});
    setTimeout(() => {
      triggerTransition("/");
    }, 2000);
  };

  const handleTick = (remainingTime: any) => {
    if (remainingTime <= 10) {
      const progress = remainingTime * 10;
      setProgress(progress);
    }
    return setRemainingTime(remainingTime);
  };

  useEffect(() => {
    track('Menu Accessed', {});

    animations_gsap.showElements(".stag_profile", 0.5, () => {});
    animations_gsap.fadeOut("#white_transition", 0.2, () => {
      gsap.to("#white_transition", { display: "none"});
    });
    

    const heartbeat: any = new Heartbeat(
      30 * 1000,
      handleTimeout,
      handleTick,
      setAskisngStillThere
    );

    myHeartbeat.current = heartbeat;

    heartbeat.start();

    const handleActivity = () => heartbeat.reset();

    document.addEventListener("mousemove", handleActivity);
    document.addEventListener("keydown", handleActivity);

    return () => {
      heartbeat.stop();
      document.removeEventListener("mousemove", handleActivity);
      document.removeEventListener("keydown", handleActivity);
    };
  }, []);

  useEffect(() => {
    if (goToProfile && myHeartbeat.current) {
      // sets new timeout for heartbeat
      myHeartbeat.current.setCountdown(50 * 1000);
      myHeartbeat.current.reset();

      triggerTransition(`/profile/${goToProfile}`);
    }
  }, [goToProfile]);

  return (
    <div id="pathway_menu" className="relative flex flex-col h-screen">

      <div id="white_transition" className="bg-white h-full w-full fixed z-40"></div>

      <Debugger setShowDebugger={setShowDebugger} showDebugger={showDebugger} />

      <DebuggerGUI
        showDebugger={showDebugger}
        setShowDebugger={setShowDebugger}
        setHashTableProfiles={setHashTableProfiles}
        hashTableProfiles={hashTableProfiles}
      />

      <img
        src="./assets/bg_menu.png"
        className="absolute object-cover w-full h-full select-none"
      ></img>

      {askisngStillThere && (
        <div
          id="pop_up_still_there"
          className="absolute w-full h-full bg-slate-950 bg-opacity-60 z-50 flex justify-center items-center"
        >
          {!redirectUser && (
            <div className="w-1/4 h-1/4 bg-white rounded-b-[33px] flex flex-col justify-evenly items-center bg-still-there relative">
              <div className="w-full bg-[#9FD6BF] rounded-none h-[4px] absolute top-0">
                <div
                  className="bg-yellow-gradient h-[4px] rounded-none transition-all duration-500 "
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <h1 className="text-[45px] text-white font-avenir font-black">
                Still there?
              </h1>
              <button className="w-auto px-14 h-20 rounded-full bg-buttonsMenu right-8 bottom-[-2rem] z-20 border-white border-4 flex justify-center items-center text-white font-avenir font-black text-[25px]">
                Yes, I’m still here
              </button>
            </div>
          )}
        </div>
      )}

      <div className="content flex flex-col w-full h-[90%] mt-6 justify-evenly items-center">
        <HeaderMenu />
        <div className="relative flex items-center w-full px-8 h-[55%] justify-center mb-10">
          {hashTableProfiles[0].isDisabled ? (
            ""
          ) : (
            <div className="w-1/3 h-full mx-4 relative stag_profile" onClick={() => setGoToProfile("JEREMY")}>
              <ProfilesMenu
                name={"jeremy"}
                age={74}
                imageSrc={"./assets/profiles/jeremy.png"}
                diagnosis={"Previously untreated: Unfit"}
                description={
                  "I would like my treatment to impact my lifestyle as little as possible."
                }
              />
              <ButtonGoProfile
                redirection={setGoToProfile}
                profile={"JEREMY"}
              />
            </div>
          )}

          {hashTableProfiles[1].isDisabled ? (
            ""
          ) : (
            <div className="w-1/3 h-full mx-4 relative stag_profile" onClick={() => setGoToProfile("ABIGAIL")}>
              <ProfilesMenu
                name={"abigail"}
                age={62}
                imageSrc={"./assets/profiles/abigail.png"}
                diagnosis={"Previously untreated: Fit"}
                description={
                  "I'm an active person who is used to going and doing as I please. When I started feeling poorly and was tired all the time, I got concerned."
                }
              />
              <ButtonGoProfile
                redirection={setGoToProfile}
                profile={"ABIGAIL"}
              />
            </div>
          )}

          {hashTableProfiles[2].isDisabled ? (
            ""
          ) : (
            <div className="w-1/3 h-full mx-4 relative stag_profile" onClick={() => setGoToProfile("MARK")}>
              <ProfilesMenu
                name={"mark"}
                age={68}
                imageSrc={"./assets/profiles/mark.png"}
                diagnosis={"Previously treated"}
                description={
                  "I'm disappointed to learn that I relapsed after all this time. I'm wondering where my treatment goes from here."
                }
              />
              <ButtonGoProfile redirection={setGoToProfile} profile={"MARK"} />
            </div>
          )}
        </div>
        <div
          id="footer_pathway_menu"
          className="w-full fixed bottom-4 flex flex-row mt-6 z-20 px-6"
        >
          <div className="w-1/3 flex justify-start items-end pb-6 stag_profile">
            <img src="./assets/footer.svg" alt="" width={320} className="ml-4" />
          </div>
          <div className="w-1/3 flex justify-center items-start py-4 stag_profile">
            <img src="./assets/footer2.svg" alt="" width={320} />
          </div>
          <div className="w-1/3 flex justify-end stag_profile">
            <img src="./assets/venclyxto_logo.png" alt="" width={280} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PathwayMenu;
